// apps/client/src/pages/settings/components/SelfInvoiceConfig.tsx
//
// Settings panel: "Allow self-referencing invoice journals" — an opt-in for
// orgs that invoice themselves to shift costs between accounts. When enabled,
// the user must pick a Xero contact representing the organisation; invoices
// to that contact are pulled via a separate sync pathway and stored with
// source='SELF_INVOICE' (counted identically to BILL rows, but ingested
// independently).

import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import {
  XeroContactSummary,
  listXeroContacts,
  updateSelfInvoiceConfig,
} from '../../../services/selfInvoices';

interface SelfInvoiceConfigProps {
  organisationId: string;
  initialEnabled: boolean;
  initialContactId: string | null;
  /** Disable while parent is mid-mutation (e.g. (dis)connecting Xero) */
  disabled?: boolean;
  onSaved?: (next: { enabled: boolean; contactId: string | null }) => void;
}

const SelfInvoiceConfig: React.FC<SelfInvoiceConfigProps> = ({
  organisationId,
  initialEnabled,
  initialContactId,
  disabled,
  onSaved,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [enabled, setEnabled] = useState<boolean>(initialEnabled);
  const [contactId, setContactId] = useState<string | null>(initialContactId);
  const [contacts, setContacts] = useState<XeroContactSummary[]>([]);
  const [loadingContacts, setLoadingContacts] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  // Lazy-load contacts the first time the section is expanded.
  useEffect(() => {
    if (!enabled) return;
    if (contacts.length > 0) return;
    setLoadingContacts(true);
    listXeroContacts(organisationId)
      .then((res) => setContacts(res))
      .catch((err) => {
        console.error(err);
        enqueueSnackbar('Failed to load Xero contacts', { variant: 'error' });
      })
      .finally(() => setLoadingContacts(false));
  }, [enabled, organisationId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side search when the user types — debounced.
  useEffect(() => {
    if (!enabled) return;
    const term = search.trim();
    const handle = setTimeout(() => {
      setLoadingContacts(true);
      listXeroContacts(organisationId, term)
        .then((res) => setContacts(res))
        .catch((err) => {
          console.error(err);
          enqueueSnackbar('Failed to search Xero contacts', { variant: 'error' });
        })
        .finally(() => setLoadingContacts(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [search, enabled, organisationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedContact = useMemo(
    () => contacts.find((c) => c.id === contactId) ?? null,
    [contacts, contactId]
  );

  const isDirty =
    enabled !== initialEnabled || (contactId ?? null) !== (initialContactId ?? null);
  const canSave = !saving && !disabled && (!enabled || !!contactId) && isDirty;

  const handleSave = async () => {
    setSaving(true);
    try {
      const next = await updateSelfInvoiceConfig(organisationId, {
        enabled,
        contactId: enabled ? contactId : null,
      });
      enqueueSnackbar(
        next.selfInvoicesEnabled
          ? 'Self-invoice journals enabled'
          : 'Self-invoice journals disabled',
        { variant: 'success' }
      );
      onSaved?.({
        enabled: next.selfInvoicesEnabled,
        contactId: next.selfXeroContactId,
      });
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        err instanceof Error ? err.message : 'Failed to save self-invoice config',
        { variant: 'error' }
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card variant="outlined">
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" gutterBottom>
              Self-Referencing Invoice Journals
            </Typography>
            <Typography variant="body2" color="text.secondary">
              If your organisation uses invoices to shift costs between accounts (an invoice with
              your own organisation as the contact, often netting to zero), enable this option so
              those invoices are pulled via a dedicated sync pathway and tagged distinctly.
              Counted identically to regular bills in all reports.
            </Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(_, v) => setEnabled(v)}
                disabled={disabled || saving}
              />
            }
            label="Allow self-referencing invoice journals"
          />

          {enabled && (
            <Box>
              <Autocomplete
                value={selectedContact}
                onChange={(_, v) => setContactId(v?.id ?? null)}
                onInputChange={(_, v) => setSearch(v)}
                options={contacts}
                loading={loadingContacts}
                getOptionLabel={(opt) => opt.name}
                isOptionEqualToValue={(opt, val) => opt.id === val.id}
                disabled={disabled || saving}
                renderOption={(props, opt) => (
                  <li {...props} key={opt.id}>
                    <Box>
                      <Typography variant="body2">{opt.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {opt.id}
                        {opt.isCustomer && opt.isSupplier ? ' • customer + supplier' : ''}
                      </Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Self contact in Xero"
                    helperText="Select the Xero contact that represents your own organisation."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {loadingContacts ? <CircularProgress size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
              {enabled && !contactId && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  Select a contact before saving.
                </Alert>
              )}
            </Box>
          )}

          <Stack direction="row" justifyContent="flex-end">
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={!canSave}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export default SelfInvoiceConfig;
